import { useQuery } from '@tanstack/react-query';

import type { ServicesProvider } from '@ekhodealer/ekho-common';

import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get a services provider by ID
 * Routes through the admin_portal namespace for centralized auth/authz
 */
export const getServicesProvider = async (servicesProviderId: string): Promise<ServicesProvider> => {
	const response = await apiClient.get<ServicesProvider>(
		configurationEndpoints.details(servicesProviderId),
	);
	return response.data;
};

/**
 * Hook for fetching a services provider by ID
 */
export const useGetServicesProvider = (id: string) => {
	return useQuery({
		queryKey: queries.servicesProviders.detail(id).queryKey,
		queryFn: () => getServicesProvider(id),
		enabled: !!id,
		staleTime: Infinity, // never stale unless the data is mutated
	});
};

/**
 * Response shape from the batch details endpoint
 */
interface BatchServicesProviderResponse {
	/** Successfully fetched service providers */
	servicesProviders: ServicesProvider[];
	/** IDs of service providers that failed to fetch */
	failedIds: string[];
}

/**
 * API function to batch fetch multiple service providers by IDs.
 * Returns partial results — individual SP failures don't fail the batch.
 */
export const batchGetServicesProviders = async (
	servicesProviderIds: string[],
): Promise<BatchServicesProviderResponse> => {
	const response = await apiClient.post<BatchServicesProviderResponse>(
		configurationEndpoints.batchDetails(),
		{ servicesProviderIds },
	);
	return response.data;
};
